// Seller, Entregas de webhook (`spec/24-croqui-alinhamento.md` §5, WEB-108). Zenith:
// igual ao frame `croqui-zenith/screens/seller/webhooks-entregas.tsx` em 1440.
//
// Foco: a TABELA de entregas. Os filtros vivem na query string (`spec/17` §3): recarregar
// a página, compartilhar o link e o botão de voltar continuam valendo. O detalhe aberto
// também, `?id=whd_…` é o que faz o drawer sobreviver ao refresh (a rota `$id` não está
// em `files_allowed` desta task; ver "Questões abertas").
//
// As abas do croqui mapeiam 1:1 no `status` do servidor, derivá-las no cliente sobre uma
// lista paginada por cursor faria "Mortas" mostrar só as mortas das 50 primeiras.

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import {
  DELIVERIES_QUERY_KEY,
  type Delivery,
  deliveriesListOptions,
  deliveryOptions,
  retryDelivery,
} from '../api/queries/webhooks.ts';
import { useMode } from '../app/mode.tsx';
import { ListToolbar } from '../components/app/list-toolbar.tsx';
import { PageHeader } from '../components/app/page-header.tsx';
import { Input } from '../components/ui/input.tsx';
import { Segmented } from '../components/ui/segmented.tsx';
import { DeliveriesTable } from '../features/webhooks/deliveries-table.tsx';
import { DeliveryDrawer } from '../features/webhooks/delivery-drawer.tsx';
import { deliveryAttemptsOptions } from '../features/webhooks/local-queries.ts';
import {
  DELIVERY_STATUSES,
  DELIVERY_TABS,
  DELIVERY_TARGETS,
  type DeliveryTarget,
  isStepUpCancelled,
  matchesSearch,
  retryErrorMessage,
  statusOfTab,
  TARGET_FILTER_LABEL,
  tabOf,
} from '../features/webhooks/model.ts';

const ALL = 'all';

/** Query string tolerante a lixo: `?status=banana` cai em `undefined` (`catch` do Zod). */
const searchSchema = z.object({
  status: z.enum(DELIVERY_STATUSES).optional().catch(undefined),
  /**
   * Filtro de ALVO (endpoint/postback/UTMify), aplicado no CLIENTE: `GET
   * /v1/me/webhook-deliveries` não aceita `target` como query. O croqui não tem esta
   * coluna nem este filtro; ficam porque `WebhookDelivery.target` é real e a tela de hoje
   * (WEB-43) já os entrega, `inventario-seller.md`, coluna `omite`.
   */
  target: z.enum(DELIVERY_TARGETS).optional().catch(undefined),
  /** Busca do croqui, também no cliente: a API não tem busca textual. */
  q: z.string().optional().catch(undefined),
  id: z.string().min(1).optional().catch(undefined),
});

type DeliveriesSearch = z.infer<typeof searchSchema>;

function targetOf(value: string): DeliveryTarget | undefined {
  return DELIVERY_TARGETS.find((target) => target === value);
}

function Deliveries() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { mode } = useMode();
  const queryClient = useQueryClient();

  const term = search.q ?? '';
  const clientFiltered = search.target !== undefined || term.trim() !== '';
  const filtered = search.status !== undefined || clientFiltered;

  const list = useInfiniteQuery(deliveriesListOptions(mode, { status: search.status }));
  const detail = useQuery(deliveryOptions(mode, search.id));
  const attempts = useQuery(
    deliveryAttemptsOptions(mode, detail.data?.event_id, detail.data?.endpoint_id),
  );

  const [resendMessage, setResendMessage] = useState<{ ok: boolean; text: string } | undefined>(
    undefined,
  );

  const loaded = (list.data?.pages ?? []).flatMap((page) => page.data);
  const rows = loaded.filter(
    (row) =>
      (search.target === undefined || row.target === search.target) && matchesSearch(row, term),
  );
  const selectedRow = loaded.find((row) => row.id === search.id);

  const apply = (next: Partial<DeliveriesSearch>): void => {
    void navigate({ search: { ...search, ...next }, replace: true });
  };

  const resend = useMutation({
    mutationFn: async (row: Delivery) => {
      setResendMessage(undefined);
      return retryDelivery(mode, row.id);
    },
    onSuccess: (response) => {
      setResendMessage({
        ok: true,
        // O reenvio é assíncrono (`202`): quem entrega é o worker (`spec/07` §4.3).
        text: `Reenvio enfileirado (${response.delivery_id}). A nova tentativa aparece na lista em alguns segundos.`,
      });
    },
    onError: (error) => {
      if (!isStepUpCancelled(error)) {
        setResendMessage({ ok: false, text: retryErrorMessage(error) });
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [DELIVERIES_QUERY_KEY] });
    },
  });

  return (
    <>
      <PageHeader
        title="Entregas de webhook"
        description="Histórico de tentativas de todos os endpoints"
      />

      <ListToolbar>
        <Input
          type="text"
          aria-label="Buscar entregas"
          placeholder="Buscar por evento ou id da entrega"
          className="max-w-105 flex-1"
          value={term}
          onChange={(event) => {
            apply({ q: event.target.value === '' ? undefined : event.target.value });
          }}
        />
        {/* `<select>` nativo, não `ui/select.tsx` (Radix): `e2e/webhooks.spec.ts` usa
            `getByLabel('Alvo').selectOption(...)`, que só funciona num elemento real
            (WEB-100 "Questões abertas" item 5 já divergiu por isso no `NavDrawer`/kit). */}
        <select
          aria-label="Alvo"
          value={search.target ?? ALL}
          onChange={(event) => {
            apply({ target: targetOf(event.target.value) });
          }}
          className="h-9 min-w-0 rounded-xl border border-transparent bg-secondary/60 px-3 text-sm shadow-(--shadow-inset) outline-none focus-visible:border-ring focus-visible:bg-card focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <option value={ALL}>Todos os alvos</option>
          {DELIVERY_TARGETS.map((target) => (
            <option key={target} value={target}>
              {TARGET_FILTER_LABEL[target]}
            </option>
          ))}
        </select>
      </ListToolbar>

      <ListToolbar right={`${rows.length} ${rows.length === 1 ? 'entrega' : 'entregas'}`}>
        <Segmented
          aria-label="Filtro das entregas"
          value={tabOf(search.status)}
          onChange={(tab) => {
            apply({ status: statusOfTab(tab) });
          }}
          options={DELIVERY_TABS.map((tab) => [tab.id, tab.label] as const)}
        />
      </ListToolbar>

      <DeliveriesTable
        rows={rows}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => {
          void list.refetch();
        }}
        hasMore={list.hasNextPage}
        isLoadingMore={list.isFetchingNextPage}
        onLoadMore={() => {
          void list.fetchNextPage();
        }}
        onSelect={(row) => {
          setResendMessage(undefined);
          apply({ id: row.id });
        }}
        selectedId={search.id}
        filtered={filtered}
        onClearFilters={() => {
          apply({ status: undefined, target: undefined, q: undefined });
        }}
      />

      <DeliveryDrawer
        open={search.id !== undefined}
        row={selectedRow}
        detail={detail.data}
        attempts={attempts.data ?? []}
        isLoading={detail.isPending}
        error={detail.error}
        onRetryLoad={() => {
          void detail.refetch();
        }}
        onClose={() => {
          setResendMessage(undefined);
          apply({ id: undefined });
        }}
        onResend={(row) => {
          resend.mutate(row);
        }}
        isResending={resend.isPending}
        {...(resendMessage === undefined ? {} : { resendMessage })}
      />
    </>
  );
}

export const Route = createFileRoute('/app/developers/webhooks/deliveries/')({
  validateSearch: searchSchema,
  component: Deliveries,
});
